import {
    RefObject,
    useMemo,
    useEffect,
    useRef,
    useState,
    ReactElement,
    KeyboardEventHandler,
    ReactNode,
    useCallback,
} from 'react';
import classnames from 'classnames';

import { keyboardKeys, keyboardMatch } from '@hh.ru/magritte-common-keyboard';
import { type TokenBaseCoreSpace } from '@hh.ru/magritte-design-tokens/types';
import { BottomSheet, BottomSheetFooter } from '@hh.ru/magritte-ui-bottom-sheet';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Button } from '@hh.ru/magritte-ui-button';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { ArrowPathOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { ControlledInputProps } from '@hh.ru/magritte-ui-input';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { renderSection } from '@hh.ru/magritte-ui-suggest/sectionRenderers';
import {
    DataProviderResult,
    SuggestProps,
    SuggestInputComponent,
    InputProp,
    SearchSubmitType,
} from '@hh.ru/magritte-ui-suggest/types';

import styles from './suggest.less';

interface SuggestPickerProps<K, P extends SuggestInputComponent = SuggestInputComponent> {
    data: DataProviderResult<K> | null | undefined;
    activatorRef: RefObject<HTMLElement>;
    inputRef: RefObject<HTMLElement>;
    onValueSelect: (value: string, data: K | undefined) => void;
    maxHeight: number;
    navigationBarProps: SuggestProps<unknown, SuggestInputComponent>['navigationBarProps'];
    input: InputProp<P>;
    inputFocused: boolean;
    currentInputValueSelectedFromSuggest: boolean;
    suggestContainerRef: RefObject<HTMLElement>;
    itemsGap: TokenBaseCoreSpace;
    onBottomSheetClose: VoidFunction;
    inputValue: string;
    topContent?: ReactNode;
    bottomContent?: ReactNode;
    dropWidth?: number;
    isError: boolean;
    trls: SuggestProps<unknown, SuggestInputComponent>['trls'];
    errorPlaceholder: ReactNode;
    onClickReload: VoidFunction;
    onBottomSheetOpen?: VoidFunction;
    onDropOpen?: VoidFunction;
    onDropClose?: VoidFunction;
    onSearchSubmit?: SearchSubmitType;
}

export const getSearchResultCount = <K,>(options: DataProviderResult<K> | null | undefined): number =>
    options?.filter(({ type, searchable = true }) => type !== 'delimiter' && searchable).length || 0;

const dropPlacement = ['bottom-left', 'top-left'];

export const SuggestPicker = function <K, P extends SuggestInputComponent>({
    data,
    activatorRef,
    inputRef,
    onValueSelect,
    maxHeight,
    navigationBarProps,
    input,
    inputFocused,
    currentInputValueSelectedFromSuggest,
    suggestContainerRef,
    itemsGap,
    onBottomSheetClose,
    onBottomSheetOpen,
    inputValue,
    topContent,
    bottomContent,
    dropWidth,
    isError,
    trls,
    errorPlaceholder,
    onClickReload,
    onDropOpen,
    onDropClose,
    onSearchSubmit,
}: SuggestPickerProps<K, P>): ReactElement | null {
    const { isMobile, breakpoint } = useBreakpoint();
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [showDrop, setShowDrop] = useState(true);
    const bottomSheetInputRef = useRef<HTMLInputElement>(null);
    const [caretVisible, setCaretVisible] = useState(false);

    useEffect(() => {
        if (inputFocused) {
            setShowBottomSheet(true);
        } else if (!isMobile) {
            setShowBottomSheet(false);
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [inputFocused, setShowBottomSheet]);

    useEffect(() => {
        if (currentInputValueSelectedFromSuggest) {
            setShowBottomSheet(false);
        }
    }, [currentInputValueSelectedFromSuggest, isMobile]);

    // если скрыли дроп из-за скрытия активатора под скроллом, то при следующем изменении данных нужно его показать
    useEffect(() => {
        setShowDrop(true);
    }, [inputValue, inputFocused]);

    const onKeyDownHandler = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            input.props.onKeyDown?.(event);
            if (onSearchSubmit && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
                const onCloseBottomSheet = () => {
                    setShowBottomSheet(false);
                };
                // в onSearchSubmit нужно вернуть количество результатов поиска
                onSearchSubmit(event, onCloseBottomSheet, getSearchResultCount(data));
            }
        },
        [input.props, onSearchSubmit, data]
    );

    const suggestContent = useMemo(() => {
        if (!data) {
            return null;
        }

        return data.reduce<Array<JSX.Element>>((result, section, index) => {
            const renderResult = renderSection(
                section,
                index,
                onValueSelect,
                breakpoint,
                data.length,
                Boolean(bottomContent)
            );
            result.push(...renderResult);
            return result;
        }, []);
    }, [data, onValueSelect, breakpoint, bottomContent]);

    const dropVisible =
        suggestContent !== null &&
        suggestContent.length > 0 &&
        !currentInputValueSelectedFromSuggest &&
        inputFocused &&
        showDrop;

    const InputComponent = input.component;
    const processInput =
        (ref: RefObject<HTMLElement>): KeyboardEventHandler<HTMLElement> =>
        (event) => {
            const hasPrintableRepresentation = event.key.replace(/\s/g, '').length === 1;
            if (!hasPrintableRepresentation) {
                return;
            }
            ref.current?.focus();
            const newEvent = new KeyboardEvent('keydown', event.nativeEvent);
            ref.current?.dispatchEvent(newEvent);
        };

    return (
        <>
            <BottomSheet
                visible={showBottomSheet}
                height="full-screen"
                onAfterExit={onBottomSheetClose}
                keyboardOverlaysContent={false}
                header={
                    <NavigationBar
                        {...navigationBarProps}
                        options={
                            <div
                                className={classnames({
                                    [styles.bottomSheetInputHideCaret]: !caretVisible,
                                })}
                            >
                                <InputComponent
                                    {...({
                                        ...input.props,
                                        invalid: false,
                                        ref: bottomSheetInputRef,
                                        autoComplete: 'off',
                                        onKeyDown: onKeyDownHandler,
                                    } as unknown as JSX.IntrinsicAttributes &
                                        JSX.LibraryManagedAttributes<P, ControlledInputProps>)}
                                />
                            </div>
                        }
                    />
                }
                onAppear={() => {
                    bottomSheetInputRef?.current?.focus();
                    setCaretVisible(true);
                    onBottomSheetOpen?.();
                }}
                onBeforeExit={() => {
                    bottomSheetInputRef?.current?.blur();
                    setCaretVisible(false);
                }}
                onClose={() => {
                    setShowBottomSheet(false);
                }}
                footer={
                    isError && (
                        <BottomSheetFooter>
                            <Button
                                mode="secondary"
                                style="negative"
                                icon={<ArrowPathOutlinedSize24 />}
                                onClick={onClickReload}
                                data-qa="suggest-reload-button"
                                stretched
                            >
                                {trls.resetButton}
                            </Button>
                        </BottomSheetFooter>
                    )
                }
                ref={suggestContainerRef}
                allowScrollWhileFocused
            >
                {!isError && (
                    <div
                        onKeyDown={processInput(bottomSheetInputRef)}
                        className={styles.suggestItemsContainer}
                        style={{ gap: itemsGap }}
                    >
                        {topContent}
                        {suggestContent}
                        {bottomContent}
                    </div>
                )}
                {isError && <div className={styles.errorWrapper}>{errorPlaceholder}</div>}
            </BottomSheet>
            <Drop
                autoFocusWhenOpened={false}
                placement={dropPlacement}
                lockPlacement
                visible={dropVisible}
                onClose={() => setShowDrop(false)}
                activatorRef={activatorRef}
                role="status"
                space={400}
                widthEqualToActivator={!dropWidth}
                width={dropWidth}
                forcePlacement
                keyboardNavigationMode="list"
                data-qa="suggest-drop"
                ref={suggestContainerRef}
                onAppear={onDropOpen}
                onAfterExit={onDropClose}
            >
                <div onKeyDown={processInput(inputRef)} className={styles.suggestItemsContainer} style={{ maxHeight }}>
                    <div className={styles.suggestItemsContainerWrapper} style={{ gap: itemsGap }}>
                        {topContent}
                        {suggestContent}
                        {bottomContent}
                    </div>
                </div>
            </Drop>
        </>
    );
};

SuggestPicker.displayName = 'SuggestPicker';
